import { useLockBodyScroll } from '@uidotdev/usehooks';
import BionicLogo from 'components/BionicUI/bionicLogo/BionicLogo';
import BPIcon, { icons } from 'components/BionicUI/icons/BPIcon';
import ProjectsNav from 'components/search/ProjectsNav';
import { useAuth } from 'context/auth.context';
import { cn } from 'lib/utils';
import { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import Notification, { Notify } from '../profileMenu/Notification';
import BPIconButton from 'components/BionicUI/icons/BPIconButton';

interface BurgerMenuContentProps {
  notifications: Notify[];
  toggleOpen: () => void;
}

const BurgerMenuContent: FC<BurgerMenuContentProps> = ({
  notifications,
  toggleOpen,
}) => {
  useLockBodyScroll();

  const { logOut } = useAuth();
  const navigate = useNavigate();

  const onClickMyProfile = () => navigate(ROUTES.PROFILE);
  const onClickLogOut = () => {
    try {
      logOut();
      navigate(ROUTES.LOGIN);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="flex fixed top-0 left-0 w-full h-full z-20 backdrop-blur-lg bg-gradient-to-b from-black/90 to-black/10 overflow-y-auto scrolling-touch">
      <div className="grid relative w-full min-h-full items-start grid-rows-[auto_1fr] animate-burger-menu-open">
        <div className="grid w-full top-0 left-0 h-[90px] items-center justify-center">
          <BionicLogo
            className={'block relative w-[128px] mt-[24px]'}
            animate={true}
          />
        </div>
        <div className="grid relative w-full p-6 gap-6 rounded-[8px] overflow-hidden text-left">
          <SectionTitle>Navigate to</SectionTitle>
          <ProjectsNav />
          <Divider />
          <div className="grid relative w-full grid-cols-[auto_1fr] gap-4">
            <MenuButton onClick={onClickMyProfile} icon="profile">
              My Profile
            </MenuButton>
            <div />
            <MenuButton onClick={onClickLogOut} icon="logout" red>
              Log Out
            </MenuButton>
            <div />
          </div>
          <Divider />
          <SectionTitle>Notifications</SectionTitle>
          {notifications?.length > 0 && (
            <div className="block relative pb-[50px]">
              <div className="grid relative gap-2 pb-[30px]">
                {notifications.map((notification, idx) => (
                  <Notification
                    key={`notification_${idx}`}
                    idx={idx}
                    notification={notification}
                  />
                ))}
              </div>
              <Divider />
            </div>
          )}
        </div>
      </div>
      <div className="block absolute top-[20px] right-[20px] z-10 animate-fade-in">
        <BPIconButton icon="close" onClick={toggleOpen} />
      </div>
    </div>
  );
};

interface MenuButtonProps {
  children: ReactNode;
  onClick: () => void;
  icon: keyof typeof icons;
  red?: boolean;
}

const MenuButton: FC<MenuButtonProps> = ({
  children,
  onClick,
  icon,
  red = false,
}) => (
  <button
    onClick={onClick}
    className="grid relative grid-cols-[auto_1fr] gap-2 items-center w-full pr-[40px] hover:opacity-50 text-left"
  >
    <BPIcon
      icon={icon}
      className={cn(red ? 'fill-bionic-red' : 'fill-white')}
    />
    <div className={cn(red ? 'text-bionic-red' : 'text-white')}>{children}</div>
  </button>
);

const SectionTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="block relative text-[10px] font-semiBold uppercase tracking-widest text-bionic-muted">
    {children}
  </div>
);

const Divider = () => (
  <div className="block relative w-full h-[1px] bg-white/20" />
);

export default BurgerMenuContent;
